import { cn, isLinkActive } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useMediaQuery } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { ThemeSwitcher } from '@sohanscript/theme/ui';
import { Input } from '@sohanscript/ui/input';
import { Motion } from '@sohanscript/ui/motion';
import { Sheet, SheetContent, SheetTrigger } from '@sohanscript/ui/sheet';
import { useLocation } from '@tanstack/react-router';
import { AnimatePresence, domAnimation, LazyMotion } from 'motion/react';
import { useState } from 'react';
import { useSohanEmon } from '#/lib/hooks/sohanemon.tsx';
import { Link } from '@/components/link';
import { Brand } from './brand';
import { LanguageSwitcher } from './language-switcher';

const EASE_NAV = 'ease-[cubic-bezier(0.65,0,0.35,1)]';

function MiniNav({ currentPath }: { currentPath: string }) {
	const { links, contact } = useSohanEmon();
	const mainLinks = links.filter((link) => !link.opts?.footerOnly);
	const isProjectCta = contact.href === '/start';

	return (
		<nav
			aria-label={m.nav_main()}
			className="corder-bevel pointer-events-auto flex h-9 max-w-full items-center gap-1 rounded-xl border border-border/80 bg-background/85 pr-1.5 pl-3 shadow-(--shadow-popover) backdrop-blur-xl transition-colors duration-300 hover:border-primary/30"
		>
			<Brand mini />
			<span aria-hidden className="h-4 w-px shrink-0 bg-border/70" />

			<ul className="mx-1 hidden items-center md:flex">
				{mainLinks.map((link, index) => {
					const label = link.label;
					const isActive =
						isLinkActive({ targetPath: link.href, currentPath }) ||
						isLinkActive({
							targetPath: link.subhref,
							currentPath,
							exact: false,
						});
					return (
						<li key={link.href} className="flex items-center">
							{index > 0 && (
								<span aria-hidden className="h-4 w-px shrink-0 bg-border/50" />
							)}
							<Link
								to={link.href as '/'}
								viewTransition={false}
								{...(link.opts ? link.opts : {})}
								className={cn(
									'relative mx-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium text-2xs tracking-wide transition-colors',
									isActive
										? 'text-primary'
										: 'text-muted-foreground hover:bg-primary/10 hover:text-primary',
								)}
							>
								{isActive && (
									<span
										aria-hidden
										className="text-[0.5rem] text-primary leading-none"
									>
										●
									</span>
								)}
								{label}
							</Link>
						</li>
					);
				})}
			</ul>

			<span
				aria-hidden
				className="hidden h-4 w-px shrink-0 bg-border/70 sm:block"
			/>
			<Link
				to={contact.href as '/'}
				viewTransition={false}
				target={contact.target}
				className="group/cta inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium font-mono text-2xs text-secondary uppercase tracking-[0.15em] transition-colors hover:bg-primary/10 hover:text-primary"
			>
				{isProjectCta ? m.cta_short() : contact.label}
				<Iconify
					icon={isProjectCta ? 'lucide:arrow-up-right' : 'lucide:external-link'}
					className="size-3 text-primary transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
				/>
			</Link>
		</nav>
	);
}

export function Navbar() {
	const [open, setOpen] = useState(false);
	const location = useLocation();
	const currentPath = location.pathname;
	const os = isLinkActive({ targetPath: '/os', currentPath });
	const isLg = useMediaQuery('lg');
	const isMd = useMediaQuery('md');
	const hasBreadcrumbPath = /^\/(service|note|work)\//.test(currentPath);

	if (os) return null;

	return (
		<LazyMotion features={domAnimation}>
			<>
				<div
					id="navbar"
					className={cn(
						'group sticky top-0 z-40 transition-transform duration-500 motion-reduce:transition-none',
						EASE_NAV,
						'has-nav-hidden:-translate-y-(--height-navbar)',
					)}
				>
					<div
						id="navbar-content"
						className="border-border/60 border-b bg-background/90 backdrop-blur-sm"
					>
						<nav
							aria-label={m.nav_main()}
							className="transition-[opacity,visibility] duration-500 has-nav-hidden:invisible has-nav-hidden:opacity-0 motion-reduce:transition-none"
						>
							<div className="container flex h-navbar items-center justify-between px-4">
								<Brand full noAnim />
								{isLg ? (
									<div className="flex items-center gap-1.5">
										<NavContent currentPath={currentPath} />
										<ThemeSwitcher compact />
										<LanguageSwitcher compact />
									</div>
								) : (
									<Sheet open={open} onOpenChange={setOpen}>
										<SheetTrigger className="text-primary">
											<Iconify icon="lucide:menu" className="size-6" />
										</SheetTrigger>
										<SheetContent
											side="right"
											className="w-80 max-w-[85vw] gap-0 p-0"
										>
											<MobileNav currentPath={currentPath} setOpen={setOpen} />
										</SheetContent>
									</Sheet>
								)}
							</div>
						</nav>
					</div>
				</div>

				<div
					className={cn(
						'pointer-events-none fixed inset-x-0 top-3 z-50 flex justify-center px-4 transition-all duration-500 motion-reduce:transition-none',
						EASE_NAV,
						'invisible -translate-y-4 opacity-0',
						'has-nav-hidden:visible has-nav-hidden:translate-y-0 has-nav-hidden:opacity-100',
						hasBreadcrumbPath && isMd && 'top-8',
					)}
				>
					<MiniNav currentPath={currentPath} />
				</div>
			</>
		</LazyMotion>
	);
}

function MobileNav({
	currentPath,
	setOpen,
}: {
	currentPath: string;
	setOpen: (state: boolean) => void;
}) {
	const [query, setQuery] = useState('');
	const links = useSohanEmon().links.filter((l) => !l.opts?.footerOnly);
	const year = new Date().getFullYear();
	const pwd =
		currentPath === '/' ? 'index' : currentPath.replace(/^\/+|\/+$/g, '');

	const isActive = (link: (typeof links)[number]) =>
		isLinkActive({ targetPath: link.href, currentPath }) ||
		isLinkActive({ targetPath: link.subhref, currentPath, exact: false });

	const filtered = links.filter((link) => {
		const needle = query.trim().toLowerCase();
		if (!needle) return true;
		const haystack = `${link.label} ${link.href} ${link.subhref}`.toLowerCase();
		return haystack.includes(needle);
	});

	return (
		<nav className="flex h-full flex-col" aria-label={m.nav_main()}>
			<Link
				to="/"
				viewTransition={false}
				onClick={() => setOpen(false)}
				className="flex items-baseline gap-2 border-border/60 border-b px-4 py-4"
			>
				<code className="font-mono text-foreground-light text-sm">
					<span className="text-primary">~/</span> {pwd}
				</code>
				<span className="font-mono text-[0.58rem] text-muted-foreground">
					nav
				</span>
			</Link>

			<div className="px-3 pt-3 pb-2">
				<label htmlFor="nav-search" className="sr-only">
					{m.nav_search()}
				</label>
				<div className="relative">
					<Iconify
						icon="lucide:search"
						className="pointer-events-none absolute inset-s-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
					/>
					<Input
						id="nav-search"
						type="search"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={m.nav_search_placeholder()}
						className="py-1.5 ps-8 pe-3 font-mono text-xs"
					/>
				</div>
			</div>

			<ul className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pt-1 pb-4">
				{filtered.map((link) => {
					const active = isActive(link);
					return (
						<li key={link.href}>
							<Link
								to={link.href as '/'}
								viewTransition={false}
								onClick={() => setOpen(false)}
								aria-current={active ? 'true' : undefined}
								{...link.opts}
								className={cn(
									'group flex items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 transition-colors',
									active
										? 'border-primary/25 bg-primary/8 text-foreground-light'
										: 'text-muted-foreground hover:bg-background-light hover:text-foreground-light',
								)}
							>
								<Iconify
									icon={link.icon ?? 'lucide:folder'}
									className={cn(
										'size-3.5 shrink-0',
										active
											? 'text-primary'
											: 'text-primary/60 group-hover:text-primary',
									)}
								/>
								<span className="min-w-0 flex-1 truncate text-sm">
									{link.label}
								</span>
								<span
									className={cn(
										'whitespace-nowrap font-mono text-[0.56rem]',
										active ? 'text-primary/80' : 'text-muted-foreground/70',
									)}
								>
									{link.href}
								</span>
							</Link>
						</li>
					);
				})}
				{filtered.length === 0 && (
					<li className="px-2.5 py-2 font-mono text-muted-foreground text-xs">
						{m.nav_no_matches()}
					</li>
				)}
			</ul>

			<div className="flex items-center justify-between gap-3 border-border/60 border-t px-4 py-3">
				<p className="font-mono text-[0.6rem] text-muted-foreground">
					main <span className="text-primary">·</span> v{year}.1
				</p>
				<div className="flex items-center gap-1">
					<ThemeSwitcher compact />
					<LanguageSwitcher compact />
				</div>
			</div>
		</nav>
	);
}

const NavContent = ({ currentPath }: { currentPath: string }) => {
	const [hoveredNav, setHoveredNav] = useState<string | null>(null);
	const links = useSohanEmon().links.filter((l) => !l.opts?.footerOnly);

	const renderLink = (link: (typeof links)[number]) => {
		const label = link.label;
		const isActive =
			isLinkActive({ targetPath: link.href, currentPath }) ||
			isLinkActive({ targetPath: link.subhref, currentPath, exact: false });
		const isHovered = hoveredNav === link.labelKey;

		const showOnHover = isHovered || (isActive && hoveredNav === null);
		const showOnActive = isActive;

		return (
			<Link
				key={link.href}
				className="relative px-2.5 py-px"
				viewTransition={false}
				to={link.href as '/'}
				onMouseOver={() => setHoveredNav(link.labelKey)}
				{...link.opts}
			>
				<span
					{...(isActive ? { 'data-active': '' } : {})}
					className={cn(
						'relative z-10 text-secondary data-active:text-primary-foreground',
						isHovered && 'text-primary',
					)}
				>
					{label}
				</span>
				<AnimatePresence>
					{showOnHover && (
						<Motion
							as="span"
							layoutId="inactive-nav-items"
							className="absolute inset-0 rounded-sm ring ring-primary ring-offset-2 ring-offset-background transition-none"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>
					)}
				</AnimatePresence>
				<AnimatePresence>
					{showOnActive && (
						<Motion
							as="span"
							layoutId="current-active-nav-item"
							className="absolute inset-0 rounded-sm bg-primary transition-none"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>
					)}
				</AnimatePresence>
			</Link>
		);
	};

	return (
		<ul
			className="flex items-center gap-2.5 pe-2"
			onMouseLeave={() => setHoveredNav(null)}
		>
			{links.map((link) => (
				<li key={link.href}>{renderLink(link)}</li>
			))}
		</ul>
	);
};
